import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

import Login from './pages/Login';
import GeofenceRestricted from './pages/GeofenceRestricted';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import CreateJob from './pages/CreateJob';
import JobDetail from './pages/JobDetail';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import FaultLog from './pages/FaultLog';
import Schedule from './pages/Schedule';
import Teams from './pages/Teams';
import TeamMembers from './pages/TeamMembers';
import TeamMemberDetail from './pages/TeamMemberDetail';
import SubContractors from './pages/SubContractors';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import Assets from './pages/Assets';
import AssetDetail from './pages/AssetDetail';
import BOQManagement from './pages/BOQManagement';
import Requisitions from './pages/Requisitions';
import SiteDiary from './pages/SiteDiary';
import SiteDiaryDetail from './pages/SiteDiaryDetail';
import ClientDiaryPortal from './pages/ClientDiaryPortal';
import ComingSoon from './pages/ComingSoon';
import LookupMaintenance from './pages/LookupMaintenance';
import TimeAttendance from './pages/TimeAttendance';
import Cashflow from './pages/Cashflow';
import Profile from './pages/Profile';
import Layout from './components/Layout';
import UserManagement from './pages/UserManagement';
import ArchivedJobs from './pages/ArchivedJobs';
import POPIACompliance from './pages/POPIACompliance';
import IncidentReporting from './pages/IncidentReporting';
import SiteWorkerClock from './pages/SiteWorkerClock';
import PerformanceReviews from './pages/PerformanceReviews';
import SystemSettings from './pages/SystemSettings';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect site_worker to their dedicated clock page
  if (user.role === 'site_worker') {
    return children;
  }

  return children;
};

// Special route for site_worker only pages
const SiteWorkerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/diary-portal/:token" element={<ClientDiaryPortal />} />
          <Route path="/geofence-restricted" element={<GeofenceRestricted />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          {/* Site Worker dedicated clock page */}
          <Route
            path="/clock"
            element={
              <SiteWorkerRoute>
                <SiteWorkerClock />
              </SiteWorkerRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="fault-log" element={<FaultLog />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/new" element={<CreateJob />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="assets" element={<Assets />} />
            <Route path="assets/:id" element={<AssetDetail />} />
            <Route path="teams" element={<Teams />} />
            <Route path="team-members" element={<TeamMembers />} />
            <Route path="team-members/:id" element={<TeamMemberDetail />} />
            <Route path="subcontractors" element={<SubContractors />} />
            <Route path="boq" element={<BOQManagement />} />
            <Route path="requisitions" element={<Requisitions />} />
            <Route path="site-diary" element={<SiteDiary />} />
            <Route path="site-diary/:id" element={<SiteDiaryDetail />} />
            <Route path="time-attendance" element={<TimeAttendance />} />
            <Route path="cashflow" element={<Cashflow />} />
            <Route path="finance/purchase-orders" element={<ComingSoon title="Purchase Orders" />} />
            <Route path="finance/invoicing" element={<ComingSoon title="Invoicing" />} />
            <Route path="finance/payment-certificates" element={<ComingSoon title="Payment Certificates" />} />
            <Route path="finance/claims" element={<ComingSoon title="Claims" />} />
            <Route path="finance/payroll" element={<ComingSoon title="Payroll" />} />
            <Route path="lookup" element={<LookupMaintenance />} />
            <Route path="lookup/:type" element={<LookupMaintenance />} />
            <Route path="reports" element={<Reports />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="profile" element={<Profile />} />
            <Route path="user-management" element={<UserManagement />} />
            <Route path="archived-jobs" element={<ArchivedJobs />} />
            <Route path="popia-compliance" element={<POPIACompliance />} />
            <Route path="incident-reporting" element={<IncidentReporting />} />
            <Route path="performance-reviews" element={<PerformanceReviews />} />
            <Route path="system-settings" element={<SystemSettings />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;